import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

// CHECK THIS PATH: src/components/visual/Globe/Globe.js
import Globe from './components/visual/Globe/Globe';

// CHECK THIS PATH: src/features/auth/components/AuthForms.js
import { LoginForm, SignUpForm, OTPForm, SuccessView, UpdatePasswordForm, ForgotPasswordForm, ResetPasswordForm } from './features/auth/components/AuthForms';
import { SetPasswordForm } from './features/auth/components/SetPasswordForm';

// Dashboard
import Dashboard from './pages/Dashboard';

// Services (public page - no auth required)
import Services from './pages/Services';

// CHECK THIS PATH: src/App.module.css
import styles from './App.module.css';
import Logo from './assets/hyderotrace.svg';

// Protected Route Component
function ProtectedRoute({ children }) {
    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');

    if (!accessToken || !user) {
        return <Navigate to="/signin" replace />;
    }

    return children;
}

// Auth Pages Wrapper Component
function AuthPages() {
    const navigate = useNavigate();
    const [view, setView] = useState('signin');
    const [userEmail, setUserEmail] = useState('');
    const [activationToken, setActivationToken] = useState('');
    const [resetPasswordData, setResetPasswordData] = useState({ uid: '', token: '' });

    // Handle navigation with custom setView function
    const handleSetView = (newView, email = '') => {
        if (email) {
            setUserEmail(email);
        }

        // Map views to routes
        const routeMap = {
            'signin': '/signin',
            'signup': '/signup',
            'forgot': '/forgot-password',
            'dashboard': '/dashboard',
            'services': '/services'
        };

        if (routeMap[newView]) {
            navigate(routeMap[newView]);
        } else {
            setView(newView);
        }
    };

    const handleSetViewWithEmail = (newView, email = '') => {
        if (email) {
            setUserEmail(email);
        }
        handleSetView(newView);
    };

    // Check for reset password token in URL on mount
    useEffect(() => {
        const resetMatch = window.location.pathname.match(/\/reset-password\/([^\/]+)\/([^\/]+)/i);
        if (resetMatch) {
            const uid = resetMatch[1];
            const token = resetMatch[2];
            console.log('Reset password token found - uid:', uid, 'token:', token);
            setResetPasswordData({ uid, token });
            setView('resetpassword');
            return;
        }

        // Check for activation token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromQuery = urlParams.get('token');
        const pathMatch = window.location.pathname.match(/\/activate\/([a-f0-9\-]+)/i);
        const tokenFromPath = pathMatch ? pathMatch[1] : null;
        const token = tokenFromQuery || tokenFromPath;

        if (token) {
            console.log('Activation token found:', token);
            setActivationToken(token);
            setView('updatepassword');
        }
    }, []);

    const location = useLocation();

    // Determine which view to show based on URL
    useEffect(() => {
        const path = location.pathname;
        if (path === '/signup') setView('signup');
        else if (path === '/signin' || path === '/') setView('signin');
        else if (path === '/forgot-password') setView('forgot');
    }, [location]);

    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <Globe />
            </div>

            <div className={styles.glassCard}>
                {view !== 'success' && view !== 'updatepassword' && view !== 'resetpassword' && (
                    <div className={styles.header}>
                        <img src={Logo} alt="Hydro Trace Logo" className={styles.logo} />

                        {(view === 'signin' || view === 'signup') && (
                            <div className={styles.buttonWrap}>
                                <div className={`${styles.tabs} ${styles.newclass}`}>
                                    <button className={view === 'signin' ? styles.activeTab : styles.tabBtn} onClick={() => handleSetView('signin')}>Sign In</button>
                                    <button className={view === 'signup' ? styles.activeTab : styles.tabBtn} onClick={() => handleSetView('signup')}>Sign Up</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {view === 'signin' && <LoginForm key="1" setView={handleSetViewWithEmail} />}
                    {view === 'signup' && <SignUpForm key="2" setView={handleSetView} />}
                    {view === 'forgot' && <ForgotPasswordForm key="3" setView={handleSetView} />}
                    {view === 'otp' && <OTPForm key="4" setView={handleSetView} email={userEmail} />}
                    {view === 'updatepassword' && <UpdatePasswordForm key="5" setView={handleSetView} token={activationToken} />}
                    {view === 'resetpassword' && <ResetPasswordForm key="6" setView={handleSetView} uid={resetPasswordData.uid} token={resetPasswordData.token} />}
                    {view === 'setpassword' && <SetPasswordForm key="5b" setView={handleSetView} activationToken={activationToken} />}
                    {view === 'success' && <SuccessView key="7" setView={handleSetView} />}
                </AnimatePresence>
            </div>
        </div>
    );
}

// Dashboard Wrapper
function DashboardWrapper() {
    const navigate = useNavigate();

    const handleSetView = (view) => {
        if (view === 'signin') {
            navigate('/signin');
        } else if (view === 'services') {
            navigate('/services');
        }
    };

    return <Dashboard setView={handleSetView} />;
}

// Services Wrapper
function ServicesWrapper() {
    const navigate = useNavigate();

    const handleSetView = (view) => {
        if (view === 'signin') {
            navigate('/signin');
        } else if (view === 'signup') {
            navigate('/signup');
        } else if (view === 'dashboard') {
            navigate('/dashboard');
        }
    };

    return <Services setView={handleSetView} />;
}

function App() {
    return (
        <Router>
            <div className={styles.main}>
                <div className={styles.background}></div>
                <div className={styles.overlay}></div>

                <Routes>
                    {/* Public Routes */}
                    <Route path="/services" element={<ServicesWrapper />} />
                    <Route path="/signin" element={<AuthPages />} />
                    <Route path="/signup" element={<AuthPages />} />
                    <Route path="/forgot-password" element={<AuthPages />} />
                    <Route path="/activate/:token" element={<AuthPages />} />
                    <Route path="/reset-password/:uid/:token" element={<AuthPages />} />

                    {/* Protected Routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardWrapper />
                            </ProtectedRoute>
                        }
                    />

                    {/* Default Route */}
                    <Route path="/" element={<Navigate to="/signin" replace />} />
                </Routes>
            </div>
        </Router>
    );
}

export default App;
